import PaginationControl from "@/components/common/PaginationControl";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { MultiSelect, SelectItem } from "@/components/ui/multi-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { agentService } from "@/services/agent.service/agent.api";
import companyService from "@/services/company";
import { organizationTagsService } from "@/services/organizationTags.service/organizationTags.api";
import projectService from "@/services/project";
import { teamService } from "@/services/team.service/team.api";
import ticketService, { Ticket } from "@/services/ticket";
import { Check, CheckCheck, Filter, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { TicketPriority, TicketStatus } from '../types/ticket';

const statusColors: Record<TicketStatus, string> = {
  open: 'bg-blue-500',
  in_progress: 'bg-yellow-500',
  resolved: 'bg-green-500'
};

const STATUS_TABS: { value: TicketStatus; label: string }[] = [
  { value: "open", label: "Aberto" },
  { value: "in_progress", label: "Em Atendimento" },
  { value: "resolved", label: "Fechado" },
];

const priorityColors: Record<TicketPriority, string> = {
  low: 'bg-green-500',
  medium: 'bg-yellow-500',
  high: 'bg-orange-500',
  urgent: 'bg-red-500'
};

export default function Tickets() {
  // Filtros/paginação vivem na URL (não em useState) — assim dá pra compartilhar o link com os
  // filtros aplicados e o botão "voltar" do navegador restaura a busca anterior.
  const [searchParams, setSearchParams] = useSearchParams();
  const paramsKey = searchParams.toString();

  const searchQuery = searchParams.get("q") ?? "";
  const currentPage = Number(searchParams.get("page")) || 1;
  const itemsPerPage = Number(searchParams.get("limit")) || 10;
  // biome-ignore lint/correctness/useExhaustiveDependencies: paramsKey representa searchParams inteiro
  const statusFilter = useMemo(() => searchParams.getAll("status"), [paramsKey]);
  // A aba de status é sempre um valor único; se a URL não tiver status válido, cai em "Aberto".
  const activeStatusTab: TicketStatus =
    (statusFilter.length === 1 && STATUS_TABS.some((t) => t.value === statusFilter[0])
      ? (statusFilter[0] as TicketStatus)
      : undefined) ?? "open";
  // biome-ignore lint/correctness/useExhaustiveDependencies: paramsKey representa searchParams inteiro
  const priorityFilter = useMemo(() => searchParams.getAll("priority"), [paramsKey]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: paramsKey representa searchParams inteiro
  const teamFilter = useMemo(() => searchParams.getAll("teamId"), [paramsKey]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: paramsKey representa searchParams inteiro
  const agentFilter = useMemo(() => searchParams.getAll("agentId"), [paramsKey]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: paramsKey representa searchParams inteiro
  const clientFilter = useMemo(() => searchParams.getAll("clientId"), [paramsKey]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: paramsKey representa searchParams inteiro
  const projectFilter = useMemo(() => searchParams.getAll("projectId"), [paramsKey]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: paramsKey representa searchParams inteiro
  const companyTagsFilter = useMemo(() => searchParams.getAll("companyTags"), [paramsKey]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: paramsKey representa searchParams inteiro
  const ticketTagsFilter = useMemo(() => searchParams.getAll("ticketTags"), [paramsKey]);

  // Aplica uma ou mais mudanças de uma vez (sem empilhar histórico a cada tecla/seleção) e, a não
  // ser que a própria página esteja sendo trocada, volta pra página 1 (novo filtro pode não ter
  // resultados na página em que o usuário estava).
  const updateFilters = useCallback(
    (updates: Record<string, string | string[] | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            next.delete(key);
            if (value == null || value === "") continue;
            if (Array.isArray(value)) {
              for (const v of value) next.append(key, v);
            } else {
              next.set(key, value);
            }
          }
          if (!("page" in updates)) next.delete("page");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setSearchQuery = (value: string) => updateFilters({ q: value || null });
  const setCompanyTagsFilter = (values: string[]) => updateFilters({ companyTags: values });
  const setTicketTagsFilter = (values: string[]) => updateFilters({ ticketTags: values });
  const setActiveStatusTab = (value: string) => updateFilters({ status: [value] });
  const setPriorityFilter = (values: string[]) => updateFilters({ priority: values });
  const setTeamFilter = (values: string[]) => updateFilters({ teamId: values });
  const setAgentFilter = (values: string[]) => updateFilters({ agentId: values });
  const setClientFilter = (values: string[]) => updateFilters({ clientId: values });
  const setProjectFilter = (values: string[]) => updateFilters({ projectId: values });
  const setCurrentPage = (page: number) => updateFilters({ page: page > 1 ? String(page) : null });
  const setItemsPerPage = (limit: number) => updateFilters({ limit: limit !== 10 ? String(limit) : null, page: null });

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Estados para as opções dos filtros
  const [teams, setTeams] = useState<any[]>([]);
  const [agents, setAgents] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [availableTicketTags, setAvailableTicketTags] = useState<string[]>([]);

  const { toast } = useToast();
  const navigate = useNavigate();

  // Abre o painel de filtros já expandido quando a página é carregada com filtros na URL
  // (ex.: link compartilhado) — senão os filtros aplicados ficariam escondidos.
  const [showFilters, setShowFilters] = useState(
    () =>
      Boolean(searchQuery) ||
      companyTagsFilter.length > 0 ||
      ticketTagsFilter.length > 0 ||
      priorityFilter.length > 0 ||
      teamFilter.length > 0 ||
      agentFilter.length > 0 ||
      clientFilter.length > 0 ||
      projectFilter.length > 0,
  );

  useEffect(() => {
    loadTeams();
    loadAgents();
    loadClients();
    loadProjects();
    loadTags();
    loadTicketTags();
  }, []);

  const loadTeams = async () => {
    const [data, error] = await teamService.getAll({}, undefined, undefined);
    if (data) {
      setTeams(data.result || []);
    }
  };

  const loadAgents = async () => {
    const [data, error] = await agentService.getAll({}, undefined, undefined);
    if (data) {
      setAgents(data.result || []);
    }
  };

  const loadClients = async () => {
    const [data, error] = await companyService.getAll();
    if (data) {
      setClients(data.result || []);
    }
  };

  const loadProjects = async () => {
    const [data, error] = await projectService.getAll();
    if (data) {
      setProjects(data.result || []);
    }
  };

  const loadTags = async () => {
    const [data] = await organizationTagsService.list();
    if (data) setAvailableTags(data);
  };

  // Pool de tags de empresa/cliente (acima) é um pool à parte — não reflete o que está de verdade
  // em Ticket.tags, então o filtro "Tags do ticket" precisa da própria lista.
  const loadTicketTags = async () => {
    const [data] = await ticketService.getTicketTags();
    if (data) setAvailableTicketTags(data);
  };

  const loadTickets = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setIsLoading(true);

      const params: any = {
        page: currentPage,
        limit: itemsPerPage,
        status: [activeStatusTab] as TicketStatus[],
        priority: priorityFilter as TicketPriority[],
        teamId: teamFilter,
        agentId: agentFilter,
        clientId: clientFilter,
        projectId: projectFilter,
      };
      if (companyTagsFilter.length) params.companyTags = companyTagsFilter;
      if (ticketTagsFilter.length) params.ticketTags = ticketTagsFilter;

      if (searchQuery) params.s_and = searchQuery;

      const [data, error] = await ticketService.getAll(params);

      if (data) {
        setTickets(data.result || []);
        setTotalPages(data.qtdPages || 1);
        setTotalItems(data.total || 0);
      } else if (!opts?.silent) {
        toast({
          title: "Erro ao carregar tickets",
          description: "Não foi possível carregar a lista de tickets.",
          variant: "destructive"
        });
      }
      if (!opts?.silent) setIsLoading(false);
    },
    [
      searchQuery,
      activeStatusTab,
      priorityFilter,
      teamFilter,
      agentFilter,
      clientFilter,
      projectFilter,
      companyTagsFilter,
      ticketTagsFilter,
      currentPage,
      itemsPerPage,
      toast
    ]
  );

  useEffect(() => {
    void loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    const id = window.setInterval(() => void loadTickets({ silent: true }), 5000);
    return () => clearInterval(id);
  }, [loadTickets]);

  // Contagem por status pra cada aba — respeita os demais filtros, mas ignora o status ativo
  // (senão as outras abas sempre mostrariam 0).
  const [statusCounts, setStatusCounts] = useState<Record<TicketStatus, number>>({
    open: 0,
    in_progress: 0,
    resolved: 0,
  });

  const loadStatusCounts = useCallback(async () => {
    const baseParams: any = {
      limit: 1,
      priority: priorityFilter as TicketPriority[],
      teamId: teamFilter,
      agentId: agentFilter,
      clientId: clientFilter,
      projectId: projectFilter,
    };
    if (companyTagsFilter.length) baseParams.companyTags = companyTagsFilter;
    if (ticketTagsFilter.length) baseParams.ticketTags = ticketTagsFilter;
    if (searchQuery) baseParams.s_and = searchQuery;

    const results = await Promise.all(
      STATUS_TABS.map((tab) => ticketService.getAll({ ...baseParams, status: [tab.value] })),
    );

    setStatusCounts((prev) => {
      const next = { ...prev };
      STATUS_TABS.forEach((tab, i) => {
        const [data] = results[i];
        if (data) next[tab.value] = data.total || 0;
      });
      return next;
    });
  }, [
    searchQuery,
    priorityFilter,
    teamFilter,
    agentFilter,
    clientFilter,
    projectFilter,
    companyTagsFilter,
    ticketTagsFilter,
  ]);

  useEffect(() => {
    void loadStatusCounts();
  }, [loadStatusCounts]);

  useEffect(() => {
    const id = window.setInterval(() => void loadStatusCounts(), 5000);
    return () => clearInterval(id);
  }, [loadStatusCounts]);

  const getStatusBadge = (status: TicketStatus) => {
    switch (status) {
      case "open":
        return <Badge variant="outline" className="bg-ticket-open/10 text-ticket-open border-ticket-open/20">Aberto</Badge>;
      case "in_progress":
        return <Badge variant="outline" className="bg-ticket-pending/10 text-ticket-pending border-ticket-pending/20">Em Atendimento</Badge>;
      case "resolved":
        return <Badge variant="outline" className="bg-ticket-resolved/10 text-ticket-resolved border-ticket-resolved/20">Fechado</Badge>;
    }
  };

  const getPriorityBadge = (priority: TicketPriority) => {
    switch (priority) {
      case "low":
        return <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">Baixa</Badge>;
      case "medium":
        return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Média</Badge>;
      case "high":
        return <Badge variant="outline" className="bg-orange-500/10 text-orange-500 border-orange-500/20">Alta</Badge>;
      case "urgent":
        return <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/20">Urgente</Badge>;
    }
  };

  /** Quem tem a "bola" no ticket agora — última mensagem do cliente = precisa de resposta do agente. */
  const getWaitingBadge = (ticket: Ticket) => {
    if (ticket.status === "resolved") return null;
    const last = ticket.lastMessage;
    if (!last || last.authorType === "system") return null;
    if (last.authorType === "client") {
      return (
        <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20">
          Aguardando você
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-muted text-muted-foreground border-border">
        Aguardando cliente
      </Badge>
    );
  };

  const getParticipantsLabel = (ticket: Ticket) => {
    const names = [
      ...(ticket.agentName ? [ticket.agentName] : []),
      ...((ticket.members || []).map((m) => m.name)),
    ];
    return names.length ? names.join(", ") : "Sem responsável/participantes";
  };

  const renderParticipants = (ticket: Ticket) => (
    <div className="flex flex-wrap items-center gap-1" title={getParticipantsLabel(ticket)}>
      {ticket.agentName ? (
        <Badge variant="outline" className="text-xs">{ticket.agentName}</Badge>
      ) : (
        <span className="text-xs text-muted-foreground">Sem agente</span>
      )}
      {(ticket.members?.length || 0) > 0 && (
        <Badge variant="secondary" className="text-xs">
          +{ticket.members!.length} pessoa{ticket.members!.length > 1 ? "s" : ""}
        </Badge>
      )}
    </div>
  );

  const renderLastMessage = (ticket: Ticket) => {
    const last = ticket.lastMessage;
    if (!last) return <span className="text-xs text-muted-foreground">Sem mensagens</span>;
    const authorLabel =
      last.authorType === "system" ? "Sistema" : last.authorName || (last.authorType === "agent" ? "Agente" : "Cliente");
    return (
      <div className="min-w-0 max-w-[220px] text-xs">
        <div className="flex items-center gap-1">
          <span className="truncate font-medium">{authorLabel}</span>
          {last.authorType !== "system" && (
            <span title={last.read ? "Lida" : "Não lida"}>
              {last.read ? (
                <CheckCheck className="h-3.5 w-3.5 shrink-0 text-blue-500" />
              ) : (
                <Check className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              )}
            </span>
          )}
        </div>
        <div className="truncate text-muted-foreground">{last.preview || "(sem texto)"}</div>
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold">Tickets</h1>
        <Button onClick={() => navigate('/tickets/new')} className="w-full sm:w-auto">Novo Ticket</Button>
      </div>

      <Card>
      <CardContent className="grid gap-6 pt-6">
        {/* Abas de Status */}
        <Tabs value={activeStatusTab} onValueChange={setActiveStatusTab}>
          <TabsList>
            {STATUS_TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value} className="gap-2">
                {tab.label}
                <Badge variant="secondary" className="px-1.5 py-0 text-xs">
                  {statusCounts[tab.value]}
                </Badge>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Área de Filtros */}
        <div className="grid gap-4">
          {/* Campo de Busca e Botão de Filtros */}
          <div className="flex gap-2 w-full">
            <div className="relative w-full">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar tickets..."
                className="pl-8 w-full"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setShowFilters(!showFilters)}
              className="shrink-0"
            >
              <Filter className="h-4 w-4" />
            </Button>
          </div>

          {/* Grid de Filtros - Responsivo */}
          <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 w-full transition-all duration-200 ${showFilters ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
            <MultiSelect
              value={priorityFilter}
              onValueChange={setPriorityFilter}
              placeholder="Prioridade"
              className="w-full min-w-0"
            >
              <SelectItem value="low">Baixa</SelectItem>
              <SelectItem value="medium">Média</SelectItem>
              <SelectItem value="high">Alta</SelectItem>
              <SelectItem value="urgent">Urgente</SelectItem>
            </MultiSelect>

            <MultiSelect
              value={clientFilter}
              onValueChange={setClientFilter}
              placeholder="Empresa"
              className="w-full min-w-0"
            >
              {clients.map((client) => (
                <SelectItem key={client._id} value={client._id}>
                  {client.name}
                </SelectItem>
              ))}
            </MultiSelect>

            <MultiSelect
              value={projectFilter}
              onValueChange={setProjectFilter}
              placeholder="Projeto"
              className="w-full min-w-0"
            >
              {projects.map((project) => (
                <SelectItem key={project._id} value={project._id}>
                  {project.name}
                </SelectItem>
              ))}
            </MultiSelect>

            <MultiSelect
              value={teamFilter}
              onValueChange={setTeamFilter}
              placeholder="Equipe"
              className="w-full min-w-0"
            >
              {teams.map((team) => (
                <SelectItem key={team._id} value={team._id}>
                  {team.name}
                </SelectItem>
              ))}
            </MultiSelect>

            <MultiSelect
              value={agentFilter}
              onValueChange={setAgentFilter}
              placeholder="Agente"
              className="w-full min-w-0"
            >
              {agents.map((agent) => (
                <SelectItem key={agent._id} value={agent._id}>
                  {agent.name}
                </SelectItem>
              ))}
            </MultiSelect>

            <MultiSelect
              value={companyTagsFilter}
              onValueChange={setCompanyTagsFilter}
              placeholder="Tags da empresa"
              className="w-full min-w-0"
            >
              {availableTags.map((tag) => (
                <SelectItem key={tag} value={tag}>
                  {tag}
                </SelectItem>
              ))}
            </MultiSelect>

            <MultiSelect
              value={ticketTagsFilter}
              onValueChange={setTicketTagsFilter}
              placeholder="Tags do ticket"
              className="w-full min-w-0"
            >
              {availableTicketTags.map((tag) => (
                <SelectItem key={tag} value={tag}>
                  {tag}
                </SelectItem>
              ))}
            </MultiSelect>
          </div>
        </div>

        {/* Lista de Tickets - Mobile First */}
        <div className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-3 text-muted-foreground">Carregando tickets...</span>
            </div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum ticket encontrado
            </div>
          ) : (
            <>
              {/* Tabela para desktop */}
              <div className="hidden md:block rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Protocolo</TableHead>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Projeto</TableHead>
                      <TableHead>Título</TableHead>
                      <TableHead>Participantes</TableHead>
                      <TableHead>Última mensagem</TableHead>
                      <TableHead>Aguardando</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Prioridade</TableHead>
                      <TableHead>Data de Criação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tickets.map((ticket) => (
                      <TableRow
                        key={ticket._id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/tickets/${encodeURIComponent(ticket.protocolId)}`)}
                      >
                        <TableCell className="font-medium">
                          <Link
                            to={`/tickets/${encodeURIComponent(ticket.protocolId)}`}
                            className="hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {ticket.protocolId}
                          </Link>
                        </TableCell>
                        <TableCell>{ticket.clientName || "—"}</TableCell>
                        <TableCell>{ticket.projectName || "—"}</TableCell>
                        <TableCell>
                          <Link
                            to={`/tickets/${encodeURIComponent(ticket.protocolId)}`}
                            className="hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {ticket.title}
                          </Link>
                        </TableCell>
                        <TableCell>{renderParticipants(ticket)}</TableCell>
                        <TableCell>{renderLastMessage(ticket)}</TableCell>
                        <TableCell>{getWaitingBadge(ticket)}</TableCell>
                        <TableCell>{getStatusBadge(ticket.status)}</TableCell>
                        <TableCell>{getPriorityBadge(ticket.priority)}</TableCell>
                        <TableCell>{new Date(ticket.createdAt).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false
                        }).replace(',', ' ')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Cards para mobile */}
              <div className="md:hidden space-y-4">
                {tickets.map((ticket) => (
                  <div
                    key={ticket._id}
                    className="p-4 rounded-lg border bg-card hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/tickets/${encodeURIComponent(ticket.protocolId)}`)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <Link
                        to={`/tickets/${encodeURIComponent(ticket.protocolId)}`}
                        className="font-medium hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {ticket.protocolId}
                      </Link>
                      <div className="flex flex-wrap justify-end gap-2">
                        {getWaitingBadge(ticket)}
                        {getStatusBadge(ticket.status)}
                        {getPriorityBadge(ticket.priority)}
                      </div>
                    </div>
                    <Link
                      to={`/tickets/${encodeURIComponent(ticket.protocolId)}`}
                      className="block font-semibold mb-2 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {ticket.title}
                    </Link>
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <div className="flex justify-between">
                        <span>Empresa:</span>
                        <span>{ticket.clientName || "—"}</span>
                      </div>
                      {ticket.projectName && (
                        <div className="flex justify-between">
                          <span>Projeto:</span>
                          <span>{ticket.projectName}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span>Participantes:</span>
                        {renderParticipants(ticket)}
                      </div>
                      <div className="flex justify-between items-start gap-2">
                        <span className="shrink-0">Última msg:</span>
                        {renderLastMessage(ticket)}
                      </div>
                      <div className="flex justify-between">
                        <span>Criado em:</span>
                        <span>{new Date(ticket.createdAt).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false
                        }).replace(',', ' ')}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {tickets.length > 0 && (
          <PaginationControl
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        )}
      </CardContent>
      </Card>
    </>
  );
}
